/// <amd-module name="Search/Code/Flux/ActionCreator" />
import * as React from "react";
import * as Constants from "Search/Code/Constants";
import { getItemResultKey, getInitialTake, isGitType, isTfvcType, isCustomType } from "Search/Code/Utils";
import { deserialize, stripOffRelevance } from "Search/Shared/Utils";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { IVssPageContext } from "VSS/Platform/Context";
import { onClickFPS } from "VSS/Platform/FPS";
import { ActionsHub } from "Search/Code/Flux/ActionsHub";
import { IPreviewSetting, ISortOption, IVersionControlPathService } from "Search/Shared/Base/Types";
import { PageSource } from "Search/Shared/Base/Sources/PageSource";
import { SearchSourceType } from "Search/Shared/Base/Sources/SearchSource";
import { ISearchFailedPayload } from "Search/Shared/Base/ActionsHub";
import { CodeSearchSource } from "Search/Code/Flux/Sources/CodeSearchSource";
import { IAggregatedState } from "Search/Code/Flux/StoresHub";
import { IMenuItem } from "VSSUI/Menu";
import { SearchQuery, CodeResult, CodeQueryResponse, VersionControlType } from "Search/LegacyWebApi/Contracts/Code";
import { EntitySortOption } from "Search/LegacyWebApi/Contracts/Search.SharedLegacy";
import { TelemetryWriter } from "Search/Code/Flux/Sources/TelemetryWriter";
import { SearchPerfTelemetryAdditionalData } from "Search/Shared/Base/Telemetry";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { Filter, FILTER_CHANGE_EVENT, FILTER_RESET_EVENT } from "SearchUI/Utilities/Filter";
import { scrubFilters } from "Search/Code/Flux/Stores/FilterStore";
import { SearchStatus } from "Search/Shared/Base/Stores/SearchStore";
import { CodeRefreshInfoSource } from "Search/Code/Flux/Sources/CodeRefreshInfoSource";
import { getSetting } from "Search/Shared/Base/LocalSettingsService";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { GitCommitRef } from "VC/Common/Contracts/Git";
import { RepositoryStatusResponse } from "Search/WebApi/Contracts/Search";



export interface ISources {
    searchSource: CodeSearchSource;

    pageSource: PageSource;

    refreshInfoSource: CodeRefreshInfoSource;
}

export class ActionCreator {
    private readonly timerManagement: TimerManagement;
    constructor(
        private readonly actionsHub: ActionsHub,
        private readonly sources: ISources,
        private readonly getAggregatedState: () => IAggregatedState,
        private readonly filter: Filter,
        private readonly pageContext: IVssPageContext,
        private readonly telemetryWriter: TelemetryWriter,
        private readonly serverSortEnabled: boolean,
        private readonly includeFacets: boolean,
        private readonly onFullScreenToggle: (fullScreen: boolean) => void) {
        this.timerManagement = new TimerManagement();
        this.filter.subscribe(this.applyFilter, FILTER_CHANGE_EVENT);
        this.filter.subscribe(this.resetFilters, FILTER_RESET_EVENT);
    }

    public dispose = (): void => {
        this.filter.unsubscribe(this.applyFilter, FILTER_CHANGE_EVENT);
        this.filter.unsubscribe(this.resetFilters, FILTER_RESET_EVENT);
        this.timerManagement.dispose();
    }

    public loadInitialState = (
        searchText: string,
        includeFacets: boolean,
        filters?: string,
        sortOptions?: string,
        result?: string,
        activeTab?: string,
        launchPoint?: string,
        takeResults?: string): void => {
        const { searchStoreState } = this.getAggregatedState();
        // This action is only used for logging telemetry and pivot count (when response is already in the page)
        this.actionsHub.pageInitializedWithSeedData.invoke({ activeTabKey: activeTab, launchPoint, response: searchStoreState.response });

        if (!searchStoreState.response) {
            const deserializedSortOptions = deserialize<EntitySortOption[]>(sortOptions) || [];
            const request = {
                searchText,
                skipResults: 0,
                takeResults: takeResults && !isNaN(parseInt(takeResults)) ? parseInt(takeResults) : Constants.CodeSearchTakeResults,
                sortOptions: stripOffRelevance(deserializedSortOptions, Constants.SortActionIds.Relevance),
                summarizedHitCountsNeeded: includeFacets,
                searchFilters: filters ? deserialize<{ [key: string]: string[] }>(filters) : {},
                filters: [],
                includeSuggestions: false
            } as SearchQuery;

            this.actionsHub.pageInitializationStarted.invoke({ request, activeTabKey: activeTab, launchPoint });
            this.sources
                .searchSource
                .getQueryResults(request, ({ responseWithActivityId, source }) => {
                    if (responseWithActivityId) {
                        const codeQueryResponse = responseWithActivityId.response as CodeQueryResponse;
                       
                        const activityId = responseWithActivityId.activityId && responseWithActivityId.activityId[0];
                        let activeItem;

                        if (result) {
                            activeItem = this.getActiveItem(codeQueryResponse.results.values, result);
                        }

                        this.handleResultsLoaded(request, codeQueryResponse, source, activityId, activeItem);
                        this.timerManagement.setTimeout(() => {
                            if (!includeFacets) {
                                this.actionsHub.facetsUpdated.invoke({response: codeQueryResponse});
                            }                          
                        }, 0);
                    }
                }, (error: ISearchFailedPayload<SearchQuery>) => {
                    this.actionsHub.searchFailed.invoke({ ...error, query: request });
                });
        }
    }

    public performSearch = (
        searchText: string,
        sortOptions: EntitySortOption[],
        filters: { [id: string]: string[] }): void => {
        const searchQuery = {
            searchText,
            skipResults: 0,
            takeResults: getInitialTake(this.pageContext),
            summarizedHitCountsNeeded: this.includeFacets,
            searchFilters: filters,
            filters: [],
            includeSuggestions: false,
            sortOptions
        } as SearchQuery;

        this.doSearch(searchQuery);
    }

    /**
     *  Send query on landing page scenario to update searchstore to an adequate state
     */
    public showLandingPage = (filter?: string): void => {
        const request = {
            skipResults: 0,
            takeResults: getInitialTake(this.pageContext),
            sortOptions: [],
            summarizedHitCountsNeeded: true,
            searchFilters: filter ? deserialize<{ [key: string]: string[] }>(filter) : {},
            filters: [],
            includeSuggestions: false,
            searchText: ""
        } as SearchQuery;

        this.actionsHub.pageInitializationStarted.invoke({ request, isLandingPage: true });
    }

    public openSearchInNewTab = (searchText: string): void => {
        const { searchStoreState } = this.getAggregatedState();
        this.sources.pageSource.navigateToNewSearch(Constants.EntityTypeUrlParam, searchText, searchStoreState.query.searchFilters);
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.SearchInNewTab);
    }

    public applySearchText = (searchText: string) => {
        const { searchStoreState } = this.getAggregatedState();
        const searchRequest: SearchQuery = {
            ...searchStoreState.query,
            searchText,
            skipResults: 0,
            takeResults: getInitialTake(this.pageContext),
            summarizedHitCountsNeeded: this.includeFacets
        };

        this.doSearch(searchRequest, false, false, true);
    }

    private applyFilter = (): void => {
        const currentFilterState = this.filter.getState();
        const { filterStoreState, searchStoreState } = this.getAggregatedState();
        const { filterItems } = filterStoreState;

        const filters: { [key: string]: string[] } = {};
        Object
            .keys(currentFilterState)
            .forEach((key) => {
                const value = currentFilterState[key].value;
                if (!value) {
                    return;
                }

                if (filterItems[key].enabled) {
                    filters[key] = value as string[];
                }
            });

        const searchQuery = {
            ...searchStoreState.query,
            searchFilters: scrubFilters(filters),
            skipResults: 0,
            takeResults: getInitialTake(this.pageContext),
            summarizedHitCountsNeeded: this.includeFacets
        };

        this.doSearch(searchQuery, false, true);
    }

    private resetFilters = (): void => {
        this.actionsHub.filterReset.invoke(undefined);
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.FilterResetClicked);
    }

    public onFiltersChanged = (name: string): void => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.FiltersUpdated, { name });
    }

    public toggleFilterPane = (): void => {
        this.actionsHub.filterPaneToggled.invoke(undefined);
    }

    public onRemoveSearchText = () => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.SearchTextRemoved);
    }

    public navigateToItem = (itemIndex: number, activatedOnPreviousNavigation: boolean) => {
        const { searchStoreState } = this.getAggregatedState();
        const { response } = searchStoreState;
        const items = response ? response.results.values : [];
        const item = items[itemIndex];
        this.changeActiveItem(item, activatedOnPreviousNavigation);
    }

    public changeActiveItem = (item: CodeResult, activatedOnPreviousNavigation?: boolean) => {
        const { itemContentStoreState } = this.getAggregatedState();
        const { selectedItem } = itemContentStoreState;
        if (item && item !== selectedItem) {
            this.actionsHub.itemChanged.invoke({ item, activatedOnPreviousNavigation });
        }
    }

    public clickPreviewFileNameLink = (link: string, evt: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>): void => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.PreviewFileNameClicked);

        // Instead of redirecting the browser, use Fast past switch to code hub.
        onClickFPS(this.pageContext, link, true, evt);
    }

    public onFileDownload = (): void => {
        // Fires every time the Download button in the preview pane is clicked
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.FileDownloadStarted);
    }

    public changePreviewOrienation = (previewOrientation: IPreviewSetting) => {
        this.actionsHub.previewOrientationChanged.invoke({ previewOrientation: previewOrientation });
    }

    public changeSortCriteria = ({ key, order }: ISortOption) => {
        const { searchStoreState } = this.getAggregatedState();
        const { response, query } = searchStoreState;
        const entitySortOption: EntitySortOption = { field: key, sortOrder: order };

        if (this.shouldPerformServerSort(response, key)) {
            const entitySortOptions: EntitySortOption[] =
                key !== Constants.SortActionIds.Relevance
                    ? [{
                        field: key,
                        sortOrder: order
                    }]
                    : [],
                searchQuery: SearchQuery = {
                    ...query,
                    skipResults: 0,
                    takeResults: getInitialTake(this.pageContext),
                    sortOptions: entitySortOptions,
                    summarizedHitCountsNeeded: this.includeFacets
                };
            this.doSearch(searchQuery, false);
        } else {
            const items = response ? response.results.values : [];
            this.sortItems(items, entitySortOption);
            this.actionsHub.sortOptionChanged.invoke({ sortedItems: items, sortOption: entitySortOption });
        }
    }

    public notifyFeedbackMailLinkClicked = (): void => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.FeedbackMailLinkClicked);
    }

    public clickFeedbackLink = (): void => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.DataRefreshInfoClicked);
        this.queryDataRefreshInfo(this.getAggregatedState().searchStoreState.query, this.getAggregatedState().searchStoreState.response?.results.values[0]);
    }

    public clickContextualMenuItem = (menuItem: IMenuItem): void => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.ContextualMenuItemClicked, { menuItem: menuItem.text });

        if (menuItem.id === Constants.ItemCommandKeys.download) {
            this.downloadFile(menuItem.data.item);
        }
    }

    public notifyResultsRendered = (): void => {
        const { searchStoreState } = this.getAggregatedState();
        const data: SearchPerfTelemetryAdditionalData = {
            responseActivityId: searchStoreState.activityId,
            searchSource: SearchSourceType[searchStoreState.source]
        };

        this.telemetryWriter.notifyResultsRendered(data);
    }

    public notifySearchFailed = (): void => {
        this.telemetryWriter.notifySearchFailed();
    }

    public publishZeroData = (scenarioType: string, error?: any): void => {
        this.telemetryWriter.publish(
            Constants.CustomerIntelligenceConstants.ZeroData,
            {
                scenarioType,
                error
            });
    }

    public publishRefreshInfoData = (scenarioType: string, error?: any): void => {
        this.telemetryWriter.publish(
            Constants.CustomerIntelligenceConstants.RefreshInfoData,
            {
                scenarioType,
                error
            });
    }

    public publishNotificationBanner = (scenarioType: string): void => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.NotificationBanner, { scenarioType });
    }

    public clickAccountButton = (): void => {
        const { searchStoreState } = this.getAggregatedState();
        this.sources.pageSource.openCollectionSearch(Constants.EntityTypeUrlParam, searchStoreState.query.searchText, this.includeFacets);
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.AccountButtonClicked);
    }

    public dismissNotificationBanner = (): void => {
        // Add: Error notification banner dismissed telemetry.
    }

    public clickLearnMoreLinkForPartialResults = (): void => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.LearnMoreOnPartialCodeResultsClicked);
    }

    public onPreviewLoaded = (isSuccess: boolean): void => {
        // ToDo: Add preview load telemetry.
    }

    public fetchMoreResults = (newSkipResults: number, newTakeResults: number) => {
        const { searchStoreState } = this.getAggregatedState();

        if (searchStoreState.searchStatus !== SearchStatus.Loading) {
            const searchQuery: SearchQuery = {
                ...searchStoreState.query,
                skipResults: newSkipResults,
                takeResults: newTakeResults,
                summarizedHitCountsNeeded: this.includeFacets
            };

            this.doSearch(searchQuery, true);
        }
    }

    public clickLeftPaneFileNameLink = (link: string, evt: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>): void => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.LeftPaneFileNameClicked);

        // Instead of redirecting the browser, use Fast past switch to code hub.
        onClickFPS(this.pageContext, link, true, evt);
    }

    public changeActiveTab = (activeTabKey: string, changeOnNavigation: boolean = false) => {
        const { previewStoreState } = this.getAggregatedState();
        if (previewStoreState.activeTabKey !== activeTabKey) {
            this.actionsHub.activeTabChanged.invoke({ activeTabKey });
        }
    }

    public toggleFullScreen = (isFullScreen: boolean): void => {
        const { previewStoreState } = this.getAggregatedState();
        if (isFullScreen !== previewStoreState.isFullScreen) {
            this.actionsHub.fullScreenToggled.invoke(isFullScreen);

            this.onFullScreenToggle(isFullScreen);
        }
    }

    public changeSplitterFixedSize = (newFixedSize: number): void => {
        this.actionsHub.splitterFixedSizeChanged.invoke(newFixedSize);
    }

    private shouldPerformServerSort(response: CodeQueryResponse | undefined, sortOptionKey: string): boolean {
        // Perform server sort even if the previous response was null(coulbe a error scenario)
        return (this.serverSortEnabled &&
            (!response ||
                response.results.count > response.results.values.length)) ||
            sortOptionKey === Constants.SortActionIds.Relevance;
    }

    private handleResultsLoaded = (
        searchQuery: SearchQuery,
        response: CodeQueryResponse,
        source: SearchSourceType,
        activityId: string,
        activeItem?: CodeResult): void => {
        const items: CodeResult[] = response.results.values;
        const { appliedEntitySortOption } = this.getAggregatedState();
        // Sort only for non-relevance sort action ids if server side sorting is not enabled.
        if (!this.serverSortEnabled && appliedEntitySortOption.field !== Constants.SortActionIds.Relevance) {
            this.sortItems(items, appliedEntitySortOption);
        }
        // If no item is activated by default, first item is the one which needs to be activated.
        activeItem = activeItem || items[0];

        this.actionsHub.resultsLoaded.invoke({ response, activeItem, activityId, source });
        if (isFeatureFlagEnabled(this.pageContext, Constants.FeatureFlagConstants.BranchStalenessBanner, false))
            this.queryBranchStalenessInfo(searchQuery, response.results.values[0].vcType);
        
    }

    public onFilterClick = (): void => {
        const { searchStoreState, filterStoreState } = this.getAggregatedState();
        if(filterStoreState.filtersLoading){
            this.fetchFacets(searchStoreState.query);
        }
    }

    private queryBranchStalenessInfo = (searchQuery: SearchQuery, vcType: VersionControlType): void => {      
        if (searchQuery.searchFilters[Constants.FilterKeys.ProjectFiltersKey] && searchQuery.searchFilters[Constants.FilterKeys.RepositoryFiltersKey]?.length == 1) {
            var threshold = new Date();
            var thresholdDays = getSetting<number>(this.pageContext, Constants.BranchStalenessThresholdInDays, 3);
            if (isCustomType(vcType))
                thresholdDays = getSetting<number>(this.pageContext, Constants.SDBranchStalenessThresholdInDays, 3);
            threshold.setDate(threshold.getDate() - thresholdDays);
            if (isGitType(vcType)) {
                this.sources.refreshInfoSource.getGitRefreshInfo(searchQuery.searchFilters[Constants.FilterKeys.ProjectFiltersKey][0], searchQuery.searchFilters[Constants.FilterKeys.RepositoryFiltersKey][0]).then(
                    (res: RepositoryStatusResponse) => {
                        var processedTime = res.indexedBranches[0].lastProcessedTime;
                        var branch = res.indexedBranches[0].name;
                        var commitId = res.indexedBranches[0].lastIndexedChangeId;
                        if (searchQuery.searchFilters[Constants.FilterKeys.BranchFiltersKey]) {
                            res.indexedBranches.forEach((item) => {
                                if (item.name.toLocaleLowerCase().localeCompare(searchQuery.searchFilters[Constants.FilterKeys.BranchFiltersKey][0].toLocaleLowerCase(), navigator.language) === 0) {
                                    processedTime = item.lastProcessedTime;
                                    branch = item.name;
                                    commitId = item.lastIndexedChangeId;
                                }
                                return;
                            });
                        }
                        // if last processed time within threshold, dont call the commit batch api       
                        if (processedTime < threshold) {
                            this.sources.refreshInfoSource.getBranchData(branch, searchQuery.searchFilters[Constants.FilterKeys.RepositoryFiltersKey][0], searchQuery.searchFilters[Constants.FilterKeys.ProjectFiltersKey][0], processedTime.toUTCString()).then(
                                (response: GitCommitRef[]) => {
                                    if (response.length == 0) {
                                        //do nothing, this is the condition which represents no staleness in the branch
                                        return;
                                    }
                                    if (response.length == 1 && response[0].commitId == commitId)
                                    {
                                        //Filtering out the indexed commit id if returned from the commits batch API
                                        return;
                                    }
                                    else if (response[response.length - 1].author.date < threshold) {
                                        this.actionsHub.StaleBranchBannerLoaded.invoke();
                                    }
                                }
                            ).catch(error => { this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.FetchGitCommitBatch, { error }) });
                        }
                    }).catch(error => { this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.QueryStalenessInfo, { error }) });
            }
            if (isTfvcType(vcType)) {
                this.sources.refreshInfoSource.getTFVCRefreshInfo(searchQuery.searchFilters[Constants.FilterKeys.ProjectFiltersKey][0]).then(res => {
                    var lastIndexedTime = res.indexingInformation[0].lastProcessedTime;
                    if (lastIndexedTime < threshold) {
                        this.sources.refreshInfoSource.getTFVCChangeData(lastIndexedTime.toUTCString(), searchQuery.searchFilters[Constants.FilterKeys.ProjectFiltersKey][0]).then(TFVCChange => {
                            if (TFVCChange.length == 0 || (TFVCChange.length == 1 && TFVCChange[0].changesetId.toString() == res.indexingInformation[0].lastIndexedChangeId)) {
                                //do nothing, this is the condition which represents no staleness in the branch
                                return;
                            }
                            else if (TFVCChange[TFVCChange.length - 1].createdDate < threshold) {
                                this.actionsHub.StaleBranchBannerLoaded.invoke();
                            }
                        }).catch(error => { this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.FetchTFVCChangeSets, { error }) });
                    }
                }).catch(error => { this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.QueryStalenessInfo, { error }) });
            }
            if (isCustomType(vcType)) {
                this.sources.refreshInfoSource.getCustomRefreshInfo(searchQuery.searchFilters[Constants.FilterKeys.ProjectFiltersKey][0], searchQuery.searchFilters[Constants.FilterKeys.RepositoryFiltersKey][0]).then(CustomChange => {
                    var lastIndexedTime = CustomChange.indexedTopLevelFolders[0].indexedBranches[0].lastProcessedTime;
                    CustomChange.indexedTopLevelFolders.forEach(function (topLevelFolder) {
                        if (topLevelFolder.indexedBranches) {
                            topLevelFolder.indexedBranches.forEach(function (branchInfo) {
                                if (branchInfo.name.localeCompare(searchQuery.searchFilters[Constants.FilterKeys.RepositoryFiltersKey][0], navigator.language) === 0) {
                                    if (branchInfo.lastProcessedTime > lastIndexedTime) {
                                        lastIndexedTime = branchInfo.lastProcessedTime;
                                    }
                                    return;
                                }
                            })
                        }
                    });
                    if (lastIndexedTime < threshold && searchQuery.searchFilters[Constants.FilterKeys.BranchFiltersKey]) {
                        this.sources.refreshInfoSource.getCustomChangeData(searchQuery.searchFilters[Constants.FilterKeys.ProjectFiltersKey][0], searchQuery.searchFilters[Constants.FilterKeys.RepositoryFiltersKey][0], searchQuery.searchFilters[Constants.FilterKeys.BranchFiltersKey][0]).then(CustomChangeData => {
                            if ((CustomChangeData.lastIndexedChangeId <= 0 && CustomChangeData.latestChangeId <= 0)) {
                                //Initially data may not be present
                                return;
                            }
                            if (CustomChangeData.lastIndexedChangeIdChangeTime == CustomChangeData.latestChangeIdChangeTime) {
                                //do nothing as either the branch is fully indexed or initially data may not be present
                                return;
                            }

                            if (CustomChangeData.latestChangeIdChangeTime < threshold) {
                                this.actionsHub.StaleBranchBannerLoaded.invoke();
                            }
                        }).catch(error => { this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.FetchCustomChangeData, { error }) });
                    }

                }).catch(error => { this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.QueryStalenessInfo, { error })});
            }
        }
    }

    private fetchFacets = (request: SearchQuery): void => {
        const requestWithIncludeFacets = { ...request, summarizedHitCountsNeeded: true, takeResults: 0 };
        this.sources
            .searchSource
            .getFacets(requestWithIncludeFacets, (responseWithActivityId) => {
                if (responseWithActivityId) {
                    const codeQueryResponse = responseWithActivityId.response as CodeQueryResponse;
                    this.actionsHub.facetsLoaded.invoke({ response: codeQueryResponse });
                }
            }, () => {
                this.actionsHub.fetchfacetsFailed.invoke({ query: request });
            });
    }

    private doSearch(
        request: SearchQuery,
        fetchMoreScenario?: boolean,
        filterApplication?: boolean,
        searchTextModified?: boolean): void {
        this.actionsHub.searchStarted.invoke({ request, fetchMoreScenario, filterApplication, searchTextModified });
        // Strip off 'relevance' sort field
        request.sortOptions = stripOffRelevance(request.sortOptions, Constants.SortActionIds.Relevance);
        this.sources
            .searchSource
            .getQueryResults(request, ({ responseWithActivityId, source }) => {
                if (responseWithActivityId) {
                    const codeQueryResponse = responseWithActivityId.response as CodeQueryResponse;
                    const activityId = responseWithActivityId.activityId && responseWithActivityId.activityId[0];

                    let activeItem: CodeResult | undefined = undefined;
                    if (fetchMoreScenario) {
                        activeItem = this.getAggregatedState().itemContentStoreState.selectedItem;
                    }

                    this.handleResultsLoaded(request, codeQueryResponse, source, activityId, activeItem);
                    if(!this.includeFacets && !filterApplication){
                        this.actionsHub.facetsUpdated.invoke({response: codeQueryResponse});
                    }
                  
                    this.timerManagement.setTimeout(() => {
                            if (!this.includeFacets && filterApplication) {    
                                this.fetchFacets(request);    
                        }
                         
                        }, 0);
                    }
            }, (error: ISearchFailedPayload<SearchQuery>) => {
                this.actionsHub.searchFailed.invoke({ ...error, query: request });
            });
    }

    /**
     * ItemKey should be undefined in all cases except URL sharing scenarios.
     * Returns either the first element on show more scenario or item identified by itemKey or the first item in the result set.
     */
    private getActiveItem = (newItems: CodeResult[], itemKey?: string): CodeResult => {
        const activeItems = newItems.filter(item => getItemResultKey(item) === itemKey);

        return activeItems.length ? activeItems[0] : newItems[0];
    }

    private sortItems = (items: CodeResult[], { field, sortOrder }: EntitySortOption): void => {
        items.sort((first: CodeResult, second: CodeResult) => {
            // using localeIgnoreCaseComparer so that it works for all locales.
            const key = field as keyof CodeResult;
            const f = first[key].toString();
            const s = second[key].toString();
            const compareValue = localeIgnoreCaseComparer(f, s);
            return sortOrder === "desc" ? -compareValue : compareValue;
        });
    }

    private downloadFile = (selectedItem: CodeResult) => {
        const { project, repositoryId, path, fileName, contentId, repository } = selectedItem;
        const versionControlPathService = this.pageContext.getService<IVersionControlPathService>("IVersionControlPathService");
        let url: string | undefined;

        if (isGitType(selectedItem.vcType)) {
            url = versionControlPathService.getGitItemContentUrl(project, repositoryId, path);
        } else if (isTfvcType(selectedItem.vcType)) {
            url = versionControlPathService.getTfvcItemContentUrl(project, path);
        } else {
            url = versionControlPathService.getCustomItemContentUrl(project, repository, path, fileName, contentId);
        }

        if (url) {
            this.sources.pageSource.openUrlInNewtab(url);
            this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.FileDownloadStarted);
        }
    }

    public queryDataRefreshInfo =  (query: SearchQuery, result?: CodeResult):void => {
       
        if (query.searchFilters[Constants.FilterKeys.ProjectFiltersKey] && query.searchFilters[Constants.FilterKeys.RepositoryFiltersKey]?.length == 1) {
            if (result && isGitType(result.vcType)) {
                this.sources.refreshInfoSource.getGitRefreshInfo(query.searchFilters[Constants.FilterKeys.ProjectFiltersKey][0], query.searchFilters[Constants.FilterKeys.RepositoryFiltersKey][0]).then(res => {
                    var commitId = res.indexedBranches[0].lastIndexedChangeId;
                    var lastProcessedTime =  new Date (Math.max.apply(null, res.indexedBranches.map(function (e) {
                        return e.lastProcessedTime.getTime();
                    })));
                    if (query.searchFilters[Constants.FilterKeys.BranchFiltersKey]) {                       
                        res.indexedBranches.forEach((item) => {
                            if (item.name.toLocaleLowerCase() == query.searchFilters[Constants.FilterKeys.BranchFiltersKey][0].toLocaleLowerCase()) {
                                commitId = item.lastIndexedChangeId;                             
                            }
                            return;
                        });
                    }                   
                    this.sources.refreshInfoSource.getGitCommit(commitId, query.searchFilters[Constants.FilterKeys.RepositoryFiltersKey][0], query.searchFilters[Constants.FilterKeys.ProjectFiltersKey][0]).then(commit => {
                        this.actionsHub.DataRefreshInfoLoaded.invoke({ lastIndexedTime: lastProcessedTime, commitId: commitId, commit: commit.author.date })
                    }).catch(error => { this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.FetchGitCommit, { error }) });
                }).catch(error => { this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.RefreshInfoData, { error }) });
            }
            if (result && isTfvcType(result.vcType)) {
                this.sources.refreshInfoSource.getTFVCRefreshInfo(query.searchFilters[Constants.FilterKeys.ProjectFiltersKey][0]).then(res => {
                    var changeid = res.indexingInformation[0].lastIndexedChangeId;
                    var lastIndexedTime = res.indexingInformation[0].lastProcessedTime;
                    this.sources.refreshInfoSource.getTfvcCommit(Number(changeid), query.searchFilters[Constants.FilterKeys.ProjectFiltersKey][0]).then(TFVCChange => {
                        this.actionsHub.DataRefreshInfoLoaded.invoke({ commitId: changeid, lastIndexedTime: lastIndexedTime, commit: TFVCChange.createdDate })
                    }).catch(error => {  this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.FetchTFVCCommit, { error }) });;
                }).catch(error => {  this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.RefreshInfoData, { error }) });
            }
            if (result && isCustomType(result.vcType) && query.searchFilters[Constants.FilterKeys.BranchFiltersKey]) {
                this.sources.refreshInfoSource.getCustomChangeData(query.searchFilters[Constants.FilterKeys.ProjectFiltersKey][0], query.searchFilters[Constants.FilterKeys.RepositoryFiltersKey][0], query.searchFilters[Constants.FilterKeys.BranchFiltersKey][0]).then(res => {
                    var changeid = res.lastIndexedChangeId;
                    var commitDate = res.lastIndexedChangeIdChangeTime;
                    this.sources.refreshInfoSource.getCustomRefreshInfo(query.searchFilters[Constants.FilterKeys.ProjectFiltersKey][0], query.searchFilters[Constants.FilterKeys.RepositoryFiltersKey][0]).then(CustomChange => {
                        var lastIndexedTime = CustomChange.indexedTopLevelFolders[0].indexedBranches[0].lastProcessedTime;
                        CustomChange.indexedTopLevelFolders.forEach(function (topLevelFolder) {
                            if (topLevelFolder.indexedBranches) {
                                topLevelFolder.indexedBranches.forEach(function (branchInfo) {
                                    if (branchInfo.name.localeCompare(query.searchFilters[Constants.FilterKeys.RepositoryFiltersKey][0], navigator.language) === 0) {
                                        if (branchInfo.lastProcessedTime > lastIndexedTime) {
                                            lastIndexedTime = branchInfo.lastProcessedTime;
                                        }
                                        return;
                                    }
                                })
                            }
                        });
                        this.actionsHub.DataRefreshInfoLoaded.invoke({ lastIndexedTime: lastIndexedTime, commitId: changeid.toString(), commit: commitDate })
                    }).catch(error => { this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.RefreshInfoData, { error }) });
                }).catch(error => { this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.FetchCustomChangeData, { error }) });
            }
        }
    }   
}